import { Flex, Image, Text } from '@chakra-ui/react';
import HowItWorksDrawer from '@ui/components/HowItWorksDrawer';
import { useDefaultAppBar } from '@ui/components/MobileAppBarProvider';
import { InDesktop, InMobile } from '@ui/components/MobileOrDesktop';
import { SEOTemplate } from '@ui/components/SEO';
import WithWallet from '@ui/components/Wallet/WithWallet';
import { getPathInfo, useRealPathname } from '@ui/hooks/router';
import { useIsWhitelistFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTranslations } from '@ui/i18n';
import type { NextPage } from 'next';

import { StakeAndUseLcroDesktop } from '../StakeAndUseLcro/StakeAndUseLcroDesktop';
import { DepositAndWithdraw } from './DepositAndWithdraw';
import { DepositDrawer } from './DepositDrawer';
import { StrategyFlowDrawer } from './StrategyFlowDrawer';
import { useStrategyHowItWorks } from './strategyHowItWorks';

export const StrategyView: NextPage = () => {
  useDefaultAppBar();
  const isStrategyVaultEnabled = useIsWhitelistFeatureEnabled('strategyVault');
  const strategyHowItWorks = useStrategyHowItWorks();
  const pathInfo = getPathInfo(useRealPathname());
  const isWithdraw = pathInfo.strategyView === 'withdraw';

  const t = useTranslations();

  if (!isStrategyVaultEnabled) {
    return null;
  }
  return (
    <>
      <SEOTemplate
        title={
          isWithdraw
            ? 'Withdraw from CRO Liquidity Strategy | Reclaim Assets & Yields'
            : 'Maximize Yields with CRO Liquidity Strategy | Automated Passive Income'
        }
        description={
          isWithdraw
            ? 'Withdraw your deposited assets and earned yields anytime from the CRO Liquidity Strategy. This automated yield maximizer puts your crypto to work earning passive income, while still allowing flexible withdrawals. Reclaim your CRO, WCRO, LCRO, and earned yields with just a few clicks.'
            : 'Maximize your crypto yields with automated liquidity provision, reward harvesting, and compound interest. Deposit CRO, WCRO, or LCRO into the CRO Liquidity Strategy to put your assets to work earning passive income across multiple opportunities on Veno.'
        }
      />
      <InMobile>
        <Flex mb={0.5} gap={2} align="center" justifyContent="center">
          <Text textStyle="caption">{t('Liquidity strategy powered by')}</Text>
          <Image w="76px" h="16px" src="/images/ferro-logo.png" alt="Ferro" />
        </Flex>
        <HowItWorksDrawer
          {...strategyHowItWorks}
          transparentOverlayHeightPx={115}
        />
        <WithWallet
          connectHint={t('Connect you wallet to see the strategy vaults')}
          minH="572px"
          connectWalletProps={{
            mt: '30px',
          }}
        >
          <DepositAndWithdraw />
        </WithWallet>
      </InMobile>
      <InDesktop>
        <StakeAndUseLcroDesktop />
      </InDesktop>
      <DepositDrawer />
      <StrategyFlowDrawer />
    </>
  );
};
